<template>
  <div class="aniversariantes-table">
    <data-table
      ref="dataTableRef"
      :config="tableConfig"
      :data="rowData"
      :refreshButton="refreshButtonConfig"
      @refresh="emit('refresh')"
      @data-loaded="handleDataLoaded"
      @data-error="handleDataError"
      @button-click="handleButtonClick">
    </data-table>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import DataTable from '@/grid/DataTable.vue';
import { eventBus } from '@/eventBus';
import { log } from '@/utils/DebugLogger';

type Id = number | string;

interface Aniversariante {
  cliente_id: Id;
  cliente_nome?: string;
  cliente_ddi?: string;
  cliente_ddd?: string;
  cliente_telefone?: string;
  cliente_data_nascimento?: string;
  idade?: number | string;
  enviado?: boolean | number;
  [key: string]: unknown;
}

interface CellParams {
  data: Record<string, unknown> & { _isFrozenFooter?: boolean };
  value?: unknown;
}

interface DataTableInstance {
  loadData: () => void;
}

interface ButtonClickPayload {
  value: unknown;
  data: Aniversariante;
  action: string;
}

const { t } = useI18n();

const props = withDefaults(defineProps<{
  aniversariantes?: Aniversariante[];
  loading?: boolean;
  franquiaId: Id | null;
}>(), {
  aniversariantes: () => [],
  loading: false
});

const emit = defineEmits<{
  refresh: [];
  'send-whatsapp': [aniversariante: Aniversariante];
}>();

const dataTableRef = ref<DataTableInstance | null>(null);

const rowData = computed<Aniversariante[]>(() => {
  return props.aniversariantes;
});

// DataTable Configuration
const tableColumns = computed(() => [
  {
    data: 'cliente_nome',
    title: t('Cliente'),
    width: 200,
    resizable: true,
    sortable: true,
    dataType: 'text'
  },
  {
    data: 'idade',
    title: t('Idade'),
    width: 100,
    resizable: true,
    sortable: true,
    dataType: 'text'
  },
  {
    data: 'cliente_data_nascimento',
    title: t('Data de Nascimento'),
    width: 130,
    resizable: true,
    sortable: true,
    renderType: 'date',
    dataType: 'date'
  },
  {
    data: 'cliente_telefone',
    title: t('Telefone'),
    width: 150,
    resizable: true,
    sortable: false,
    dataType: 'text',
    cellRenderer: (params: CellParams) => {
      const row = params.data as Aniversariante;
      const { cliente_ddi, cliente_ddd, cliente_telefone } = row;
      if (!cliente_telefone) return '-';
      return `${cliente_ddi || ''} (${cliente_ddd || ''}) ${cliente_telefone}`;
    }
  },
  {
    data: 'enviado',
    title: t('Status'),
    width: 120,
    resizable: true,
    sortable: true,
    dataType: 'text',
    filterType: 'select',
    filterOptions: [
      { label: t('Enviado'), value: true },
      { label: t('Pendente'), value: false },
    ],
    cellRenderer: (params: CellParams) => {
      if (params.value) {
        return `<span class="p-badge p-badge-success">${t('Enviado')}</span>`;
      }
      return `<span class="p-badge p-badge-warning">${t('Pendente')}</span>`;
    }
  },
  {
    data: null,
    title: t('Ações'),
    width: 120,
    resizable: false,
    sortable: false,
    renderType: 'action_buttons',
    buttons: [
      {
        text: t('Enviar'),
        icon: 'pi pi-whatsapp',
        class: 'p-button-success',
        condition: (row: Record<string, unknown>) => !row['enviado'],
        action: 'send-whatsapp'
      },
      {
        text: t('Enviado'),
        icon: 'pi pi-check',
        class: 'p-button-secondary',
        disabled: true,
        condition: (row: Record<string, unknown>) => Boolean(row['enviado']),
        action: 'none'
      }
    ]
  }
]);

const tableConfig = computed(() => ({
  tableId: 'tabelaAniversariantes',
  pageLength: 25,
  showFilters: true,
  filterDisplay: 'row',
  sortable: true,
  columns: tableColumns.value,
  pagination: true,
  stripedRows: true,
  showGridlines: true,
  resizableColumns: true,
  size: 'small',
  heightMode: 'fill-parent',
  loading: props.loading,
  rowTestId: (row: Record<string, unknown>) => {
    const id = row['cliente_id'];
    return id == null ? '' : `aniversariantes-row-${id}`;
  }
}));

// `show` is the canonical field on RefreshButtonConfig; refresh is
// signalled via the @refresh emit wired on <data-table> below.
const refreshButtonConfig = computed(() => ({
  show: true,
}));

const handleDataLoaded = (data: unknown[]): void => {
  log('AniversariantesTable', 'handleDataLoaded', 'Data loaded', data);
};

const handleDataError = (error: unknown): void => {
  log('AniversariantesTable', 'handleDataError', 'Error', error);
};

const handleButtonClick = (buttonData: ButtonClickPayload): void => {
  log('AniversariantesTable', 'handleButtonClick', 'Button clicked', buttonData);

  if (buttonData?.action === 'send-whatsapp') {
    emit('send-whatsapp', buttonData.data);
  }
};

const handleRefresh = (): void => {
  log('AniversariantesTable', 'eventBus', 'Refresh event received, triggering table refresh');
  emit('refresh');
};

onMounted(() => {
  log('AniversariantesTable', 'onMounted', 'Listening for aniversariantes.refresh events');
  eventBus.on('aniversariantes.refresh', handleRefresh);
});

onUnmounted(() => {
  log('AniversariantesTable', 'onUnmounted', 'Removing eventBus listener');
  eventBus.off('aniversariantes.refresh', handleRefresh);
});
</script>
